import { useMemo } from 'react';
import { 
  Circle, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  PlayCircle,
  Pause
} from 'lucide-react';
import type { Row_issues, Row_users, Row_projects } from '@/types/databaseRows';
import { cn } from '@/lib/utils';
import type { IssueStatus, IssuePriority } from '@/constants/issueConstants';

export interface IssueInlinePreviewProps {
  issue: Row_issues & {
    assignee?: Row_users;
    project?: Row_projects;
  };
  onClick?: (issueId: string) => void;
  isActive?: boolean;
  showStatus?: boolean;
  showPriority?: boolean;
  showAssignee?: boolean;
  showProject?: boolean;
  className?: string;
  'data-testid'?: string;
}

// Status icon mapping
const statusIcons: Record<IssueStatus, React.ElementType> = {
  backlog: Circle,
  todo: Circle,
  'in progress': PlayCircle,
  'in review': Pause,
  done: CheckCircle2,
  canceled: XCircle,
  triage: AlertCircle,
};

// Status color classes (using theme-aware colors)
const statusColorClasses: Record<IssueStatus, string> = {
  backlog: 'text-muted-foreground',
  todo: 'text-blue-500',
  'in progress': 'text-yellow-500',
  'in review': 'text-purple-500',
  done: 'text-green-500',
  canceled: 'text-destructive',
  triage: 'text-orange-500',
};

// Status badge classes (for alternate display)
const statusBadgeClasses: Record<IssueStatus, string> = {
  backlog: 'bg-muted text-muted-foreground',
  todo: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'in progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'in review': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  done: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  triage: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

// Priority color classes
const priorityColorClasses: Record<IssuePriority, string> = {
  high: 'text-red-600 dark:text-red-400',
  medium: 'text-orange-600 dark:text-orange-400',
  low: 'text-blue-600 dark:text-blue-400',
  not_set: 'text-muted-foreground',
};

function normalizeStatus(status: string): IssueStatus {
  // Normalize different representations of status
  const normalized = status.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ');
  return normalized as IssueStatus;
}

function formatStatus(status: string): string {
  // Format status for display - lowercase for consistency with tests
  return normalizeStatus(status);
}

function formatPriority(priority: string): string {
  // Format priority for display - lowercase for consistency with tests
  return priority.replace(/_/g, ' ').toLowerCase();
}

export default function IssueInlinePreview({
  issue,
  onClick,
  isActive = false,
  showStatus = true,
  showPriority = false,
  showAssignee = false,
  showProject = false,
  className,
  'data-testid': dataTestId,
}: IssueInlinePreviewProps) {
  const normalizedStatus = useMemo(() => normalizeStatus(issue.status), [issue.status]);
  const StatusIcon = statusIcons[normalizedStatus] || Circle;
  const statusColorClass = statusColorClasses[normalizedStatus] || 'text-muted-foreground';
  const priorityColorClass = priorityColorClasses[issue.priority as IssuePriority] || 'text-muted-foreground';

  const handleClick = () => {
    if (onClick) {
      onClick(issue.id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex items-center gap-3 p-2 rounded-md transition-colors',
        onClick && 'cursor-pointer hover:bg-accent',
        isActive && 'bg-accent',
        className
      )}
      data-testid={dataTestId}
    >
      {/* Status icon */}
      {showStatus && (
        <StatusIcon 
          className={cn('h-4 w-4 shrink-0', statusColorClass)} 
          aria-label={`Status: ${formatStatus(issue.status)}`}
        />
      )}

      {/* Issue title and metadata */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div 
            className="font-medium text-foreground truncate" 
            data-testid={dataTestId ? `${dataTestId}-field-title` : undefined}
          >
            {issue.title}
          </div>
        </div>
        
        {/* Show priority inline */}
        {showPriority && (
          <span 
            className={cn('text-xs font-medium shrink-0', priorityColorClass)} 
            data-testid={dataTestId ? `${dataTestId}-field-priority` : undefined}
          >
            {formatPriority(issue.priority)}
          </span>
        )}
        
        {/* Show assignee inline */}
        {showAssignee && (
          <span 
            className="text-xs text-muted-foreground shrink-0" 
            data-testid={dataTestId ? `${dataTestId}-field-assignee` : undefined}
          >
            {issue.assignee ? issue.assignee.name : 'Unassigned'}
          </span>
        )}
        
        {/* Show project inline */}
        {showProject && issue.project && (
          <span 
            className="text-xs text-muted-foreground shrink-0" 
            data-testid={dataTestId ? `${dataTestId}-field-project` : undefined}
          >
            {issue.project.name}
          </span>
        )}
      </div>

      {/* Status badge */}
      {showStatus && (
        <span 
          className={cn(
            'inline-block px-2 py-1 rounded-md text-xs font-medium shrink-0',
            statusBadgeClasses[normalizedStatus]
          )}
          data-testid={dataTestId ? `${dataTestId}-field-status` : undefined}
        >
          {formatStatus(issue.status)}
        </span>
      )}
    </div>
  );
}